import { ClipboardList } from "lucide-react";

// QC link with reliable "single-tab-per-match" behavior.
//
// Why this isn't just <a target="name">: the HTML target-name attribute
// is *supposed* to reuse a previously-named tab when clicked again,
// but in practice Chrome (and some Edge / Brave builds) open a brand-
// new tab anyway when rel="noopener" is set or the previously-opened
// tab has navigated. We saw multiple QC tabs stacking after repeat
// clicks even on the new bundle.
//
// What works reliably is the JS window.open(url, name) form: the
// browser unambiguously looks up an existing window with that name
// and reuses it; the returned window reference can then be .focus()'d
// to bring it to the foreground. We still render a real anchor so
// middle-click and ctrl/cmd-click new-tab still work — those default
// to native browser behaviour and bypass the onClick path.
export default function QcLink({ matchId, label = "QC", className, title, variant = "ribbon" }) {
  const url = `/ball/qc/${matchId}`;
  const name = `zcd_qc_${matchId}`;

  const onClick = (e) => {
    // Modifier-clicks (ctrl / cmd / shift) and non-primary buttons
    // fall through to the native anchor so users who explicitly want
    // a fresh tab keep getting one.
    if (e.button !== 0) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    try {
      const w = window.open(url, name);
      // window.open returns null if a popup blocker intervened; in
      // that case fall back to navigating the current tab so the
      // operator isn't stuck.
      if (w) {
        try { w.focus(); } catch { /* cross-tab focus may be blocked */ }
      } else {
        window.location.assign(url);
      }
    } catch {
      window.location.assign(url);
    }
  };

  const baseCls = variant === "ribbon"
    ? "rounded-md text-[10px] font-semibold uppercase tracking-wider px-2 py-1 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 flex gap-1 items-center justify-center no-underline"
    : "text-sm px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 text-emerald-300 no-underline";

  return (
    <a
      href={url}
      target={name}
      rel="noopener"
      onClick={onClick}
      className={className || baseCls}
      title={title || "Open QC. Clicking again brings the same QC tab back to focus instead of opening a new one. Ctrl/⌘+click for a new tab."}
    >
      {variant === "ribbon" ? <ClipboardList className="w-3 h-3" /> : null}
      {label}
    </a>
  );
}
